import React, { useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'
import { MarkerClusterer } from '@googlemaps/markerclusterer'

import { Customer } from 'types/Customer'

import Marker from 'pages/Dashboard/CriticalAndMajorAlertsMapCard/Marker'
import DealerMarker from 'pages/Dashboard/CriticalAndMajorAlertsMapCard/DealerMarker'

import { useSessionContext } from 'hooks/SessionContext'

type Props = {
  customersWithLocation: Array<Customer>
}

const CriticalAndMajorAlertsMap = ({ customersWithLocation }: Props) => {
  const { session: dealer } = useSessionContext()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    async function initMap () {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary

      if (ref.current && customersWithLocation && dealer) {
        const map = new google.maps.Map(ref.current, {
          center: { lat: dealer.latitude, lng: dealer.longitude },
          zoom: 8,
          mapId: 'MAP_ID'
        })

        const infoWindow = new google.maps.InfoWindow({
          content: '',
          disableAutoPan: true
        })

        const markers = customersWithLocation.filter((customerWithLocation) => (customerWithLocation.latitude && customerWithLocation.longitude)).map((customerWithLocation) => {
          // invalid Latitude and longitude has been filtered out already above

          const position = { lat: customerWithLocation.latitude!, lng: customerWithLocation.longitude! }
          const criticalAlertCount = Math.max(...customerWithLocation.devices.map((d) => d.criticalAlertCount || 0))
          const majorAlertCount = Math.max(...customerWithLocation.devices.map((d) => d.majorAlertCount || 0))
          const sdaMajorAlertCount = Math.max(...customerWithLocation.devices.map((d) => d.sdaMajorAlertCount || 0))
          const iconImg = document.createElement('img')

          iconImg.src = ''
          if (criticalAlertCount) {
            iconImg.src = 'img/dashboard-map-critical-pin.png'
          } else if (majorAlertCount) {
            iconImg.src = 'img/dashboard-map-major-pin.png'
          } else if (sdaMajorAlertCount) {
            iconImg.src = 'img/dashboard-map-sda-pin.png'
          }

          const marker = new AdvancedMarkerElement({
            position,
            content: iconImg,
            map
          })

          marker.addListener('click', () => {
            const content = ReactDOMServer.renderToString(<Marker markerInfo={customerWithLocation} />)
            infoWindow.setContent(content)
            infoWindow.open({
              anchor: marker,
              map
            })
          })

          return marker
        })

        const centerMarkerImg = document.createElement('img')
        centerMarkerImg.src = '/img/dashboard-map-dealer-pin.png'
        const centerMarker = new AdvancedMarkerElement({
          position: { lat: dealer.latitude, lng: dealer.longitude },
          content: centerMarkerImg,
          map
        })

        const dealerInfoWindow = new google.maps.InfoWindow({
          content: '',
          disableAutoPan: false
        })

        centerMarker.addListener('click', () => {
          const content = ReactDOMServer.renderToString(<DealerMarker dealer={dealer} />)
          dealerInfoWindow.setContent(content)
          dealerInfoWindow.open(map, centerMarker)
        })

        const markerclusterer = () => new MarkerClusterer({ markers, map })
        markerclusterer()
      }
    }
    initMap()
  }, [ref, customersWithLocation, dealer])

  return (
    <div ref={ref} style={{ width: '100%', height: '500px', marginBottom: '-5px' }} />
  )
}

export default CriticalAndMajorAlertsMap
